import { Box } from '@mui/material';
import { PieChart } from '@mui/x-charts';
import { FC } from 'react';
import { useGetIdentity, useGetOne, useTranslate } from 'react-admin';

import { GetDistributorDashboardCompaniesEmployeesDistributionEndpoint } from '@Common/types/bo_external/distributor/dashboard';

import { ShowSection } from '@sh4res/bo-common/components';
import {
  LegendLabel,
  PieCenterLabel,
} from '@sh4res/bo-common/components/B2bCompanyDashboard/utils';

export const CompaniesEmployeesDistribution: FC = () => {
  const translate = useTranslate();
  const { data: identity } = useGetIdentity();

  const { data } = useGetOne<
    GetDistributorDashboardCompaniesEmployeesDistributionEndpoint['Client']['ResponseBody']
  >(
    'distributor/dashboard/companies_employees_distribution',
    {
      id: identity?.id as number,
    },
    {
      enabled: !!identity,
    },
  );

  if (!data) {
    return;
  }

  const companiesPieData = [
    {
      id: 'active',
      value: data.companies.active_count,
      label: translate(
        'resources.distributor.dashboard.companies_employees_distribution.companies.active',
      ),
      color: '#594FF4',
    },
    {
      id: 'inactive',
      value: data.companies.inactive_count,
      label: translate(
        'resources.distributor.dashboard.companies_employees_distribution.companies.inactive',
      ),
      color: 'rgba(89, 79, 244, 0.2)',
    },
  ];

  const employeesPieData = [
    {
      id: 'active',
      value: data.employees.active_count,
      label: translate(
        'resources.distributor.dashboard.companies_employees_distribution.employees.active',
      ),
      color: '#594FF4',
    },
    {
      id: 'inactive',
      value: data.employees.inactive_count,
      label: translate(
        'resources.distributor.dashboard.companies_employees_distribution.employees.inactive',
      ),
      color: 'rgba(89, 79, 244, 0.2)',
    },
  ];

  return (
    <Box>
      <ShowSection
        label="resources.distributor.dashboard.companies_employees_distribution.companies.title"
        noCard={true}>
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ paddingLeft: '60px' }}>
            <PieChart
              series={[
                {
                  data: companiesPieData,
                  innerRadius: 135,
                  valueFormatter: (entry) => String(entry.value),
                },
              ]}
              height={350}
              width={400}
              slotProps={{
                legend: {
                  hidden: true,
                },
              }}>
              <PieCenterLabel
                total={data.companies.active_count + data.companies.inactive_count}
                label={
                  'resources.distributor.dashboard.companies_employees_distribution.companies.total'
                }
                width={310}
                height={340}
              />
            </PieChart>
          </Box>
          <Box
            component="ul"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
            }}>
            {companiesPieData.map((entry, idx) => (
              <LegendLabel
                key={idx}
                id={String(idx)}
                color={entry.color}
                label={entry.label}
                value={`${entry.value} (${computePercentage(entry.value, data.companies.total_count)}%)`}
              />
            ))}
          </Box>
        </Box>
      </ShowSection>
      <ShowSection
        label="resources.distributor.dashboard.companies_employees_distribution.employees.title"
        noCard={true}>
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ paddingLeft: '60px' }}>
            <PieChart
              series={[
                {
                  data: employeesPieData,
                  innerRadius: 135,
                  valueFormatter: (entry) => String(entry.value),
                },
              ]}
              height={350}
              width={400}
              slotProps={{
                legend: {
                  hidden: true,
                },
              }}>
              <PieCenterLabel
                total={data.employees.active_count + data.employees.inactive_count}
                label={
                  'resources.distributor.dashboard.companies_employees_distribution.employees.total'
                }
                width={310}
                height={340}
              />
            </PieChart>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
            }}>
            {employeesPieData.map((entry, idx) => (
              <LegendLabel
                key={idx}
                id={String(idx)}
                color={entry.color}
                label={entry.label}
                value={`${entry.value} (${computePercentage(entry.value, data.employees.total_count)}%)`}
              />
            ))}
          </Box>
        </Box>
      </ShowSection>
    </Box>
  );
};

function computePercentage(value: number, total: number) {
  return total > 0 ? Math.round((value * 100) / total) : 0;
}
